import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
    Loader2,
    AlertTriangle,
    Download,
    ChevronLeft,
    ChevronRight,
    ZoomIn,
    ZoomOut,
    Maximize2,
} from "lucide-react";
import {
    clampZoom,
    clampPage,
    PDF_ZOOM_MIN,
    PDF_ZOOM_MAX,
    PDF_ZOOM_STEP,
} from "@/lib/uploadPreview";

// Phase M follow-up — host the pdfjs worker locally from the React
// public/ folder so it stays in lock-step with the installed
// pdfjs-dist version without requiring an internet round-trip.
// (See scripts/postinstall — `public/pdf.worker.min.mjs` is copied
// from node_modules/pdfjs-dist/build at install time.)
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

// Re-export the clamp constants/helpers under their original names so
// other modules (and tests) can pick them up here too if convenient.
export { clampZoom, clampPage, PDF_ZOOM_MIN, PDF_ZOOM_MAX, PDF_ZOOM_STEP };

/**
 * PdfPreview — renders a PDF blob URL (or any same-origin URL) inline as
 * react-pdf canvases. Replaces the previous `<iframe>` approach to avoid
 * the browser's built-in PDF viewer painting a dark-gray background
 * outside the page bounds.
 *
 * Props:
 *  - src: string URL (blob: URL recommended — fetch the PDF yourself
 *         with the auth token, then pass `URL.createObjectURL(blob)`).
 *  - downloadHref: optional URL handed to the "Download PDF" fallback
 *         link when the inline render fails (and to the toolbar's
 *         Download button when `toolbar` is enabled).
 *  - downloadName: filename for the fallback / toolbar download link.
 *  - testid: data-testid root prefix.
 *  - toolbar: when truthy, render the per-page navigation + zoom + download
 *         toolbar. Defaults to FALSE so existing call sites (contracts,
 *         invoices, template uploads) keep their current "render every
 *         page top-to-bottom" UX. The Document Preview modal opts in.
 */
export default function PdfPreview({
    src,
    downloadHref,
    downloadName = "document.pdf",
    testid = "pdf-preview",
    toolbar = false,
}) {
    const [numPages, setNumPages] = useState(null);
    const [error, setError] = useState(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [page, setPage] = useState(1);
    const [zoom, setZoom] = useState(1);
    const [fitToWidth, setFitToWidth] = useState(true);
    // When react-pdf (pdfjs) fails to render a PDF — common for scanned
    // paper contracts with unusual encoding, encrypted PDFs, or PDF 2.0
    // features — we fall back to the browser's native PDF viewer via an
    // <iframe>. Chrome/Firefox/Safari all handle a much wider PDF spec
    // than pdfjs, so this turns most "Could not preview PDF" errors
    // into a usable preview.
    const [useIframeFallback, setUseIframeFallback] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!wrapperRef.current) return;
        const el = wrapperRef.current;
        const measure = () => setContainerWidth(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Reset state when the src changes (new invoice version etc).
    useEffect(() => {
        setNumPages(null);
        setError(null);
        setPage(1);
        setZoom(1);
        setFitToWidth(true);
        setUseIframeFallback(false);
    }, [src]);

    const goPrev = useCallback(() => setPage((p) => clampPage(p - 1, numPages)), [numPages]);
    const goNext = useCallback(() => setPage((p) => clampPage(p + 1, numPages)), [numPages]);

    // Keyboard arrow nav for the toolbar variant only. Scoped to this
    // component's wrapper so it doesn't hijack global arrow keys.
    useEffect(() => {
        if (!toolbar) return;
        const el = wrapperRef.current;
        if (!el) return;
        const onKey = (e) => {
            if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
            if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
        };
        el.addEventListener("keydown", onKey);
        return () => el.removeEventListener("keydown", onKey);
    }, [toolbar, goPrev, goNext]);

    if (!src) {
        return (
            <div
                className="w-full bg-white rounded-md border border-slate-200 px-6 py-10 text-center text-sm text-slate-500"
                data-testid={`${testid}-empty`}
            >
                <Loader2 className="inline-block animate-spin mr-2" size={14} />
                Generating preview…
            </div>
        );
    }

    // Native <iframe> fallback. Triggered when react-pdf/pdfjs throws on a
    // PDF the browser's own viewer can still render (scanned paper
    // contracts, encrypted PDFs, etc.). Loses the react-pdf toolbar by
    // design — the user still has Download via the parent dialog footer.
    if (useIframeFallback) {
        return (
            <div
                ref={wrapperRef}
                className="w-full bg-white rounded-md border border-slate-200 overflow-hidden"
                data-testid={testid}
            >
                <iframe
                    src={src}
                    title={downloadName}
                    data-testid={`${testid}-iframe`}
                    className="block w-full h-[78vh] bg-white"
                />
            </div>
        );
    }

    const onZoomIn = () => { setFitToWidth(false); setZoom((z) => clampZoom(z + PDF_ZOOM_STEP)); };
    const onZoomOut = () => { setFitToWidth(false); setZoom((z) => clampZoom(z - PDF_ZOOM_STEP)); };
    const onFitWidth = () => { setFitToWidth(true); setZoom(1); };

    // Width passed to <Page>: when fit-to-width is on, fill the container;
    // otherwise multiply container width by zoom factor.
    const pageWidth = containerWidth > 0
        ? (fitToWidth ? containerWidth : containerWidth * zoom)
        : undefined;

    return (
        <div
            ref={wrapperRef}
            tabIndex={toolbar ? 0 : -1}
            className="w-full bg-white rounded-md border border-slate-200 overflow-hidden focus:outline-none"
            data-testid={testid}
        >
            {toolbar && (
                <div
                    className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                    data-testid={`${testid}-toolbar`}
                >
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={goPrev}
                            disabled={page <= 1 || !numPages}
                            data-testid={`${testid}-prev`}
                            className="inline-flex items-center justify-center rounded p-1 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Previous page"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <span data-testid={`${testid}-page-indicator`} className="tabular-nums">
                            Page <span data-testid={`${testid}-page-current`}>{page}</span>
                            {" / "}
                            <span data-testid={`${testid}-page-total`}>{numPages || "…"}</span>
                        </span>
                        <button
                            type="button"
                            onClick={goNext}
                            disabled={!numPages || page >= numPages}
                            data-testid={`${testid}-next`}
                            className="inline-flex items-center justify-center rounded p-1 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Next page"
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={onZoomOut}
                            disabled={!fitToWidth && zoom <= PDF_ZOOM_MIN}
                            data-testid={`${testid}-zoom-out`}
                            className="inline-flex items-center justify-center rounded p-1 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Zoom out"
                        >
                            <ZoomOut size={14} />
                        </button>
                        <span data-testid={`${testid}-zoom-indicator`} className="tabular-nums min-w-[42px] text-center">
                            {fitToWidth ? "Fit" : `${Math.round(zoom * 100)}%`}
                        </span>
                        <button
                            type="button"
                            onClick={onZoomIn}
                            disabled={!fitToWidth && zoom >= PDF_ZOOM_MAX}
                            data-testid={`${testid}-zoom-in`}
                            className="inline-flex items-center justify-center rounded p-1 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Zoom in"
                        >
                            <ZoomIn size={14} />
                        </button>
                        <button
                            type="button"
                            onClick={onFitWidth}
                            data-testid={`${testid}-zoom-fit`}
                            className="inline-flex items-center justify-center rounded p-1 hover:bg-slate-200"
                            aria-label="Fit to width"
                        >
                            <Maximize2 size={14} />
                        </button>
                        {downloadHref && (
                            <a
                                href={downloadHref}
                                download={downloadName}
                                data-testid={`${testid}-toolbar-download`}
                                className="inline-flex items-center gap-1 rounded px-2 py-1 text-blue-900 hover:bg-slate-200"
                            >
                                <Download size={14} /> Download
                            </a>
                        )}
                    </div>
                </div>
            )}

            <Document
                file={src}
                onLoadSuccess={({ numPages: n }) => { setNumPages(n); setPage(1); }}
                onLoadError={(e) => {
                    // pdfjs choked on this PDF. Fall back to the browser's
                    // native PDF viewer via <iframe> — Chrome/Safari/Firefox
                    // tolerate much more than pdfjs (scanned paper contracts,
                    // encrypted PDFs, PDF 2.0 features). Logged at warn level
                    // so we can still spot regressions in console.
                    // eslint-disable-next-line no-console
                    console.warn("PdfPreview: pdfjs failed, falling back to iframe", e?.message || e);
                    setError(e?.message || "Failed to load PDF");
                    setUseIframeFallback(true);
                }}
                loading={
                    <div className="px-6 py-10 text-center text-sm text-slate-500" data-testid={`${testid}-loading`}>
                        <Loader2 className="inline-block animate-spin mr-2" size={14} />
                        Loading preview…
                    </div>
                }
                error={
                    <div
                        className="px-6 py-10 text-center text-sm text-slate-600 space-y-3"
                        data-testid={`${testid}-error`}
                    >
                        <div className="flex items-center justify-center gap-2 text-amber-700">
                            <AlertTriangle size={16} />
                            <span>Could not preview PDF.</span>
                        </div>
                        {downloadHref && (
                            <a
                                href={downloadHref}
                                download={downloadName}
                                className="inline-flex items-center gap-2 text-blue-900 hover:underline"
                                data-testid={`${testid}-error-download`}
                            >
                                <Download size={14} /> Download PDF
                            </a>
                        )}
                    </div>
                }
                noData={
                    <div className="px-6 py-10 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>
                        No PDF data.
                    </div>
                }
            >
                {!error && numPages
                    ? toolbar
                        ? (
                            <div data-testid={`${testid}-page-${page}`} className="flex justify-center">
                                <Page
                                    pageNumber={page}
                                    width={pageWidth}
                                    renderAnnotationLayer={false}
                                    renderTextLayer={false}
                                    className="!bg-white"
                                />
                            </div>
                        )
                        : Array.from({ length: numPages }, (_, i) => (
                              <div
                                  key={`page-${i + 1}`}
                                  className={
                                      // Separator between pages on multi-page PDFs.
                                      i > 0 ? "border-t border-slate-200" : ""
                                  }
                                  data-testid={`${testid}-page-${i + 1}`}
                              >
                                  <Page
                                      pageNumber={i + 1}
                                      width={containerWidth > 0 ? containerWidth : undefined}
                                      renderAnnotationLayer={false}
                                      renderTextLayer={false}
                                      className="!bg-white"
                                  />
                              </div>
                          ))
                    : null}
            </Document>
        </div>
    );
}
